// Hand-rolled RFC 8949 CBOR codec for the MLS envelope. Replaces
// the previous cbor-x dependency — the Go BE uses fxamacker/cbor
// which is strictly canonical, and cbor-x's default encoding tripped
// fxamacker's decoder with `bad_envelope: invalid envelope`.
//
// Envelope wire shape (mirrors services/api/internal/sync/envelope.go
// + ping-openmls-sdk/core/ping-core/src/message.rs):
//
//   map(9) {
//     1 (uint)  → v               (uint, expected 1)
//     2 (uint)  → conversation_id (bytes, 16)
//     3 (uint)  → epoch           (uint)
//     4 (uint)  → kind            (uint, 1..5)
//     5 (uint)  → sender_device   (bytes, 32)
//     6 (uint)  → seq             (uint)
//     7 (uint)  → hlc             (map { "wall_ms": uint, "logical": uint })
//     8 (uint)  → payload         (bytes)
//     9 (uint)  → content_hash    (bytes, 32)
//   }
//
// Only the major types we need are implemented:
//   0 = unsigned integer
//   2 = byte string
//   3 = text string
//   5 = map
// No floats, no tags, no arrays, no negative integers — the
// envelope doesn't carry any of those.

/* ------------------------------ encode ------------------------------ */

/** Returns the number of bytes needed to encode an integer header. */
function uintHeaderLen(n: number): number {
  if (n < 24) return 1;
  if (n < 0x100) return 2;
  if (n < 0x10000) return 3;
  if (n < 0x100000000) return 5;
  return 9;
}

/** Writes a CBOR major-type + length/value header into `buf` at `o`. */
function writeHeader(buf: Uint8Array, o: number, majorType: number, n: number): number {
  const base = majorType << 5;
  if (n < 24) {
    buf[o] = base | n;
    return o + 1;
  }
  if (n < 0x100) {
    buf[o] = base | 24;
    buf[o + 1] = n;
    return o + 2;
  }
  if (n < 0x10000) {
    buf[o] = base | 25;
    buf[o + 1] = (n >> 8) & 0xff;
    buf[o + 2] = n & 0xff;
    return o + 3;
  }
  if (n < 0x100000000) {
    buf[o] = base | 26;
    buf[o + 1] = (n >>> 24) & 0xff;
    buf[o + 2] = (n >>> 16) & 0xff;
    buf[o + 3] = (n >>> 8) & 0xff;
    buf[o + 4] = n & 0xff;
    return o + 5;
  }
  // 64-bit: emit as two 32-bit halves of a BigInt so we don't lose
  // precision on integers above 2^32 (epoch / seq / wall_ms can in
  // theory exceed it, though in practice MLS counters stay small).
  buf[o] = base | 27;
  const hi = Math.floor(n / 0x100000000);
  const lo = n >>> 0;
  buf[o + 1] = (hi >>> 24) & 0xff;
  buf[o + 2] = (hi >>> 16) & 0xff;
  buf[o + 3] = (hi >>> 8) & 0xff;
  buf[o + 4] = hi & 0xff;
  buf[o + 5] = (lo >>> 24) & 0xff;
  buf[o + 6] = (lo >>> 16) & 0xff;
  buf[o + 7] = (lo >>> 8) & 0xff;
  buf[o + 8] = lo & 0xff;
  return o + 9;
}

function writeUint(buf: Uint8Array, o: number, n: number): number {
  return writeHeader(buf, o, 0, n);
}

function writeBytes(buf: Uint8Array, o: number, bytes: Uint8Array): number {
  o = writeHeader(buf, o, 2, bytes.length);
  buf.set(bytes, o);
  return o + bytes.length;
}

function writeText(buf: Uint8Array, o: number, str: string): number {
  const encoded = new TextEncoder().encode(str);
  o = writeHeader(buf, o, 3, encoded.length);
  buf.set(encoded, o);
  return o + encoded.length;
}

function writeMapHeader(buf: Uint8Array, o: number, entries: number): number {
  return writeHeader(buf, o, 5, entries);
}

interface CborEnvelopeInput {
  v: number;
  conversation_id: Uint8Array;
  epoch: number;
  kind: number;
  sender_device: Uint8Array;
  seq: number;
  hlc: { wall_ms: number; logical: number };
  payload: Uint8Array;
  content_hash: Uint8Array;
}

export function encodeEnvelope(env: CborEnvelopeInput): Uint8Array {
  // Pre-compute the exact size so we allocate once and never grow.
  // 1 byte map header (9 entries fits the short form)
  // + per-entry: key header (1 byte for 1..9) + value encoding
  let size = 1; // outer map(9)

  // 9 keys, each fits in a single byte
  size += 9;

  // 1 → v (uint)
  size += uintHeaderLen(env.v);
  // 2 → conversation_id (bytes)
  size += uintHeaderLen(env.conversation_id.length) + env.conversation_id.length;
  // 3 → epoch
  size += uintHeaderLen(env.epoch);
  // 4 → kind
  size += uintHeaderLen(env.kind);
  // 5 → sender_device
  size += uintHeaderLen(env.sender_device.length) + env.sender_device.length;
  // 6 → seq
  size += uintHeaderLen(env.seq);
  // 7 → hlc map(2): outer header(1) + key "wall_ms" (1+7) + uint + key "logical" (1+7) + uint
  size += 1 + (1 + 7) + uintHeaderLen(env.hlc.wall_ms) + (1 + 7) + uintHeaderLen(env.hlc.logical);
  // 8 → payload
  size += uintHeaderLen(env.payload.length) + env.payload.length;
  // 9 → content_hash
  size += uintHeaderLen(env.content_hash.length) + env.content_hash.length;

  const buf = new Uint8Array(size);
  let o = 0;

  o = writeMapHeader(buf, o, 9);

  o = writeUint(buf, o, 1);
  o = writeUint(buf, o, env.v);

  o = writeUint(buf, o, 2);
  o = writeBytes(buf, o, env.conversation_id);

  o = writeUint(buf, o, 3);
  o = writeUint(buf, o, env.epoch);

  o = writeUint(buf, o, 4);
  o = writeUint(buf, o, env.kind);

  o = writeUint(buf, o, 5);
  o = writeBytes(buf, o, env.sender_device);

  o = writeUint(buf, o, 6);
  o = writeUint(buf, o, env.seq);

  o = writeUint(buf, o, 7);
  o = writeMapHeader(buf, o, 2);
  o = writeText(buf, o, 'wall_ms');
  o = writeUint(buf, o, env.hlc.wall_ms);
  o = writeText(buf, o, 'logical');
  o = writeUint(buf, o, env.hlc.logical);

  o = writeUint(buf, o, 8);
  o = writeBytes(buf, o, env.payload);

  o = writeUint(buf, o, 9);
  o = writeBytes(buf, o, env.content_hash);

  // If the size calc and write disagree we want a loud failure, not
  // a silent truncation.
  if (o !== size) {
    throw new Error(`cbor envelope size mismatch: predicted=${size} wrote=${o}`);
  }
  return buf;
}

/* ------------------------------ decode ------------------------------ */

interface Cursor {
  readonly buf: Uint8Array;
  o: number;
}

function readHeader(c: Cursor): { major: number; n: number } {
  const first = c.buf[c.o++];
  if (first === undefined) throw new Error('cbor decode: unexpected end of input');
  const major = first >> 5;
  const info = first & 0x1f;
  if (info < 24) return { major, n: info };
  if (info === 24) {
    const n = c.buf[c.o++];
    if (n === undefined) throw new Error('cbor decode: short 1-byte length');
    return { major, n };
  }
  if (info === 25) {
    const a = c.buf[c.o++];
    const b = c.buf[c.o++];
    if (a === undefined || b === undefined) throw new Error('cbor decode: short 2-byte length');
    return { major, n: (a << 8) | b };
  }
  if (info === 26) {
    const a = c.buf[c.o++];
    const b = c.buf[c.o++];
    const cc = c.buf[c.o++];
    const d = c.buf[c.o++];
    if (a === undefined || b === undefined || cc === undefined || d === undefined) {
      throw new Error('cbor decode: short 4-byte length');
    }
    return { major, n: a * 0x1000000 + ((b << 16) | (cc << 8) | d) };
  }
  if (info === 27) {
    const hiA = c.buf[c.o++]!;
    const hiB = c.buf[c.o++]!;
    const hiC = c.buf[c.o++]!;
    const hiD = c.buf[c.o++]!;
    const loA = c.buf[c.o++]!;
    const loB = c.buf[c.o++]!;
    const loC = c.buf[c.o++]!;
    const loD = c.buf[c.o++]!;
    const hi = hiA * 0x1000000 + ((hiB << 16) | (hiC << 8) | hiD);
    const lo = loA * 0x1000000 + ((loB << 16) | (loC << 8) | loD);
    return { major, n: hi * 0x100000000 + lo };
  }
  throw new Error(`cbor decode: unsupported additional info ${info}`);
}

function readBytes(c: Cursor): Uint8Array {
  const h = readHeader(c);
  if (h.major !== 2) throw new Error(`cbor decode: expected byte string, got major ${h.major}`);
  const out = c.buf.slice(c.o, c.o + h.n);
  c.o += h.n;
  return out;
}

function readText(c: Cursor): string {
  const h = readHeader(c);
  if (h.major !== 3) throw new Error(`cbor decode: expected text string, got major ${h.major}`);
  const slice = c.buf.slice(c.o, c.o + h.n);
  c.o += h.n;
  return new TextDecoder().decode(slice);
}

function readUint(c: Cursor): number {
  const h = readHeader(c);
  if (h.major !== 0) throw new Error(`cbor decode: expected unsigned int, got major ${h.major}`);
  return h.n;
}

export interface DecodedEnvelope {
  v: number;
  conversation_id: Uint8Array;
  epoch: number;
  kind: number;
  sender_device: Uint8Array;
  seq: number;
  hlc: { wall_ms: number; logical: number };
  payload: Uint8Array;
  content_hash: Uint8Array;
}

export function decodeEnvelope(input: Uint8Array | ArrayBuffer): DecodedEnvelope {
  const buf = input instanceof Uint8Array ? input : new Uint8Array(input);
  const c: Cursor = { buf, o: 0 };
  const map = readHeader(c);
  if (map.major !== 5) throw new Error(`cbor decode: expected map, got major ${map.major}`);

  // Initialise with zero values so partial decodes throw rather than
  // returning a half-populated envelope.
  const out: DecodedEnvelope = {
    v: 0,
    conversation_id: new Uint8Array(0),
    epoch: 0,
    kind: 0,
    sender_device: new Uint8Array(0),
    seq: 0,
    hlc: { wall_ms: 0, logical: 0 },
    payload: new Uint8Array(0),
    content_hash: new Uint8Array(0),
  };

  for (let i = 0; i < map.n; i++) {
    const key = readUint(c);
    switch (key) {
      case 1:
        out.v = readUint(c);
        break;
      case 2:
        out.conversation_id = readBytes(c);
        break;
      case 3:
        out.epoch = readUint(c);
        break;
      case 4:
        out.kind = readUint(c);
        break;
      case 5:
        out.sender_device = readBytes(c);
        break;
      case 6:
        out.seq = readUint(c);
        break;
      case 7: {
        const hlcHeader = readHeader(c);
        if (hlcHeader.major !== 5) {
          throw new Error(`cbor decode: hlc not a map (major ${hlcHeader.major})`);
        }
        for (let j = 0; j < hlcHeader.n; j++) {
          const k = readText(c);
          if (k === 'wall_ms') out.hlc.wall_ms = readUint(c);
          else if (k === 'logical') out.hlc.logical = readUint(c);
          else readUint(c); // tolerate unknown keys defensively
        }
        break;
      }
      case 8:
        out.payload = readBytes(c);
        break;
      case 9:
        out.content_hash = readBytes(c);
        break;
      default:
        // Unknown top-level key — skip the value (assume uint for safety).
        readUint(c);
    }
  }

  return out;
}
